import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  WIKI_ARTICLES,
  getWikiArticleBySlug,
  searchWikiArticles,
} from '../wiki-articles.js';
import { loadWikiDocument } from '../wiki-documents.js';
import './wiki-page.scss';

function WikiIndex() {
  const [query, setQuery] = React.useState('');
  const articles = React.useMemo(() => searchWikiArticles(query), [query]);

  return (
    <>
      <header className="wiki-page__hero">
        <div>
          <div className="wiki-page__eyebrow">Tenant operations</div>
          <h1>Operations WIKI</h1>
          <p>
            Workflow-first playbooks for daily admin operations, client reviews,
            documents, payments, support, and partner checks.
          </p>
        </div>

        <div className="wiki-page__search" role="search">
          <label htmlFor="wiki-search">Search operations WIKI</label>
          <div className="wiki-page__search-control">
            <i className="ti ti-search" aria-hidden="true"></i>
            <input
              id="wiki-search"
              type="search"
              value={query}
              placeholder="Search operations WIKI"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>
      </header>

      <section className="wiki-page__grid" aria-label="Operations WIKI articles">
        {articles.map((article) => (
          <Link
            key={article.slug}
            to={`/wiki/${article.slug}`}
            className="wiki-page__card"
          >
            <span className="wiki-page__card-category">{article.category}</span>
            <strong>{article.title}</strong>
            <span>{article.summary}</span>
          </Link>
        ))}
      </section>

      {articles.length === 0 && (
        <p className="wiki-page__empty">
          No matching operations playbooks found.
        </p>
      )}

      <footer className="wiki-page__meta">
        {WIKI_ARTICLES.length} operations playbooks
      </footer>
    </>
  );
}

function WikiArticle({ article, locale }) {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [isFallback, setIsFallback] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    setMarkdown('');
    setIsLoading(true);
    setIsFallback(false);

    loadWikiDocument({ locale, slug: article.slug })
      .then((result) => {
        if (cancelled) return;
        setMarkdown(result.markdown || '');
        setIsFallback(Boolean(result.isFallback));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [article.slug, locale]);

  return (
    <article className="wiki-page__reader">
      <div className="wiki-page__reader-header">
        <Link to="/wiki" className="btn btn-outline-secondary">
          <i className="ti ti-arrow-left me-1" aria-hidden="true"></i>
          Back
        </Link>
        <div>
          <span>{article.category}</span>
          <h1>{article.title}</h1>
          <p>{article.summary}</p>
        </div>
      </div>

      {isLoading && (
        <div className="wiki-page__loading">
          <i className="ti ti-loader-2 me-2" aria-hidden="true"></i>
          {t('common.loading')}
        </div>
      )}

      {!isLoading && isFallback && (
        <div className="wiki-page__notice">
          English content is shown while the selected translation is pending.
        </div>
      )}

      {!isLoading && !markdown.trim() && (
        <p className="wiki-page__empty">Coming soon.</p>
      )}

      {!isLoading && Boolean(markdown.trim()) && (
        <div className="wiki-page__markdown">
          <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
        </div>
      )}

      {article.relatedRoutes?.length > 0 && (
        <section className="wiki-page__related" aria-labelledby="wiki-related-sections-title">
          <h2 id="wiki-related-sections-title">Related sections</h2>
          <div className="wiki-page__related-list">
            {article.relatedRoutes.map((route) => (
              <Link key={route} to={route} className="wiki-page__related-link">
                <i className="ti ti-arrow-right" aria-hidden="true"></i>
                {route}
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function WikiPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const article = slug ? getWikiArticleBySlug(slug) : null;

  React.useEffect(() => {
    if (slug && !article) {
      navigate('/wiki', { replace: true });
    }
  }, [article, navigate, slug]);

  return (
    <main className="wiki-page">
      <div className="wiki-page__shell">
        {!slug && <WikiIndex />}
        {slug && article && (
          <WikiArticle
            article={article}
            locale={i18n.language || 'en'}
          />
        )}
      </div>
    </main>
  );
}

export default WikiPage;
